import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, ChevronDown, CircleAlert, LoaderCircle, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { apiError, getPublicPlans, type PlanFeature, type SubscriptionPlan } from '../lib/api';
import { config } from '../lib/config';

const money = (amount: number | undefined, currency: 'NGN' | 'USD') => {
  if (amount === undefined || amount === null) return null;
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;

  // USD is the canonical source price. Never silently approximate a bad
  // fractional source value to another whole-dollar amount.
  const maximumFractionDigits = currency === 'USD' && !Number.isInteger(value) ? 20 : 0;
  return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
};

const numberValue = (...values: Array<number | null | undefined>) => values.find(value => typeof value === 'number');

function featureLabel(feature: PlanFeature) {
  return feature.label || feature.featureLabel || feature.feature_label || feature.code || feature.featureCode || feature.feature_code || '';
}

function featureCode(feature: PlanFeature) {
  return feature.code || feature.featureCode || feature.feature_code || featureLabel(feature).toLowerCase().replace(/[^a-z0-9]+/g, '_');
}


const COMPARISON_LABELS: Record<string, string> = {
  dashboard: 'Dashboards and operational KPIs',
  members: 'Member management, onboarding and servicing',
  kyc: 'KYC review and approval workflow',
  contributions: 'Savings, share capital and contribution operations',
  loans: 'Loan products, applications and servicing',
  investments: 'Investment products and placements',
  collections: 'Payment accounts, direct debit and collections',
  approvals: 'Maker-checker approvals and decision history',
  accounting: 'General ledger, journals and trial balance',
  wallets: 'Member wallet and sub-ledger operations',
  reconciliation: 'Bank reconciliation and suspense controls',
  asset_finance: 'Asset-finance operations and asset register',
  notifications: 'Notification templates and delivery tracking',
  governance: 'Committees, AGM, voting and dividend workflows',
  risk: 'Risk scoring, fraud monitoring and investigations',
  integrations: 'Payroll, payment, banking and webhook integrations',
  reports: 'Operational, financial and audit reporting',
  settings: 'Organisation, security, role and workflow settings',
};

const CATEGORY_META: Record<string, { label: string; order: number }> = {
  OPERATIONS: { label: 'Operations and dashboards', order: 10 },
  CORE: { label: 'Operations and dashboards', order: 10 },
  MEMBERSHIP: { label: 'Member management', order: 20 },
  COMPLIANCE: { label: 'KYC and compliance', order: 30 },
  SAVINGS: { label: 'Savings and contributions', order: 40 },
  LENDING: { label: 'Loans and credit', order: 50 },
  INVESTMENTS: { label: 'Investments', order: 60 },
  INVESTMENT: { label: 'Investments', order: 60 },
  COLLECTIONS: { label: 'Payments and collections', order: 70 },
  GOVERNANCE: { label: 'Approvals and governance', order: 80 },
  FINANCE: { label: 'Accounting and financial control', order: 90 },
  ASSET_FINANCE: { label: 'Asset finance', order: 100 },
  COMMUNICATIONS: { label: 'Notifications and communication', order: 110 },
  RISK: { label: 'Risk and fraud controls', order: 120 },
  INTEGRATIONS: { label: 'Integrations', order: 130 },
  REPORTING: { label: 'Reporting and audit', order: 140 },
  ADMINISTRATION: { label: 'Administration and configuration', order: 150 },
  ADMIN: { label: 'Administration and configuration', order: 150 },
};

const categoryMeta = (category?: string) => {
  const key = String(category || 'CORE').toUpperCase();
  return CATEGORY_META[key] || { label: key.replace(/_/g, ' ').replace(/\b\w/g, value => value.toUpperCase()), order: 999 };
};

function enabledFeatures(plan: SubscriptionPlan) {
  return (plan.features || []).filter(feature => feature.enabled !== false && featureLabel(feature));
}

export default function PricingPage() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<'NGN' | 'USD'>('NGN');

  useEffect(() => {
    let active = true;
    getPublicPlans()
      .then(data => { if (active) setPlans(data.filter(plan => !plan.custom)); })
      .catch(value => { if (active) setError(apiError(value, 'Pricing is temporarily unavailable.').message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const ordered = useMemo(() => [...plans].sort((a, b) => {
    const first = numberValue(a.monthlyPriceUsd, a.monthly_price_usd, a.monthlyPriceNgn, a.monthly_price_ngn) ?? Number.MAX_SAFE_INTEGER;
    const second = numberValue(b.monthlyPriceUsd, b.monthly_price_usd, b.monthlyPriceNgn, b.monthly_price_ngn) ?? Number.MAX_SAFE_INTEGER;
    return first - second;
  }), [plans]);

  const comparisonGroups = useMemo(() => {
    const rows = new Map<string, { code: string; label: string; category: string }>();
    ordered.forEach(plan => enabledFeatures(plan).forEach(feature => {
      const code = featureCode(feature);
      rows.set(code, { code, label: COMPARISON_LABELS[code] || featureLabel(feature), category: String(feature.category || 'CORE').toUpperCase() });
    }));

    const grouped = new Map<string, { label: string; order: number; rows: Array<{ code: string; label: string }> }>();
    rows.forEach(row => {
      const meta = categoryMeta(row.category);
      const current = grouped.get(row.category) || { ...meta, rows: [] };
      current.rows.push({ code: row.code, label: row.label });
      grouped.set(row.category, current);
    });

    return [...grouped.values()]
      .sort((first, second) => first.order - second.order)
      .map(group => ({ ...group, rows: group.rows.sort((first, second) => first.label.localeCompare(second.label)) }));
  }, [ordered]);

  const planHasFeature = (plan: SubscriptionPlan, code: string) => enabledFeatures(plan).some(feature => featureCode(feature) === code);

  return <>
    <Seo title="Pricing" description="Compare live CoopCore subscription plans, operating capabilities and member capacity." path="/pricing" />
    <section className="page-hero pricing-hero section-dark"><div className="container centered-hero"><span className="section-kicker light">Live plan catalogue</span><h1>Choose the operating capacity your cooperative needs today.</h1><p>Prices, member limits and enabled capabilities are retrieved directly from CoopCore so the public catalogue stays aligned with the platform.</p><div className="currency-switch" role="group" aria-label="Pricing currency"><button className={currency === 'NGN' ? 'active' : ''} onClick={() => setCurrency('NGN')}>NGN</button><button className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button></div><p className="pricing-conversion-copy">USD is the fixed source price. NGN prices are calculated from the conversion published by the CoopCore Platform Admin.</p></div></section>
    <section className="section pricing-section"><div className="container">
      {loading ? <div className="state-card"><LoaderCircle className="spin"/><h2>Loading current plans…</h2><p>Retrieving the latest published CoopCore plans.</p></div> : null}
      {!loading && error ? <div className="state-card state-card-error"><CircleAlert/><h2>We could not load live pricing.</h2><p>{error}</p><Link className="button button-primary" to="/contact">Request current pricing</Link></div> : null}
      {!loading && !error && ordered.length === 0 ? <div className="state-card"><CircleAlert/><h2>No public plans are currently published.</h2><p>Contact CoopCore for a plan recommendation based on your member count and operating model.</p><Link className="button button-primary" to="/contact">Discuss a plan</Link></div> : null}
      <div className="pricing-grid">{ordered.map((plan, planIndex) => {
        const monthly = currency === 'NGN' ? numberValue(plan.monthlyPriceNgn, plan.monthly_price_ngn) : numberValue(plan.monthlyPriceUsd, plan.monthly_price_usd);
        const annual = currency === 'NGN' ? numberValue(plan.annualPriceNgn, plan.annual_price_ngn) : numberValue(plan.annualPriceUsd, plan.annual_price_usd);
        const maxMembers = numberValue(plan.maxMembers, plan.max_members);
        const features = enabledFeatures(plan);
        const previousPlan = planIndex > 0 ? ordered[planIndex - 1] : null;
        const addedFeatures = previousPlan
          ? features.filter(feature => !planHasFeature(previousPlan, featureCode(feature)))
          : features;
        const capabilityHeading = previousPlan
          ? `Everything in ${previousPlan.name}, plus`
          : `Included with ${plan.name}`;

        return <article className={plan.highlight ? 'pricing-card pricing-card-highlight' : 'pricing-card'} key={plan.code}>
          {plan.highlight ? <span className="popular-label">Recommended</span> : null}
          <div className="pricing-card-head"><h2>{plan.name}</h2><p>{plan.description || 'A CoopCore subscription plan configured for cooperative operations.'}</p></div>
          <div className="price-value">{monthly !== undefined ? <><strong>{money(monthly, currency)}</strong><span>/month</span></> : <strong>Contact us</strong>}</div>
          {annual !== undefined ? <p className="annual-price">Annual billing: {money(annual, currency)}</p> : null}
          <p className="member-limit">{maxMembers ? `Supports up to ${new Intl.NumberFormat().format(maxMembers)} members` : 'Member capacity agreed for your operating model'}</p>

          <div className="plan-capability-summary"><strong>{features.length} total capabilities</strong><span>{capabilityHeading}</span></div>
          <ul className="plan-added-features">{addedFeatures.map(feature => <li key={featureCode(feature)}><Check/>{featureLabel(feature)}</li>)}</ul>

          <div className="pricing-card-action"><a className={plan.highlight ? 'button button-accent button-block' : 'button button-primary button-block'} href={`${config.appUrl}/register?plan=${encodeURIComponent(plan.code)}`}>Start with {plan.name} <ArrowRight/></a></div>
        </article>;
      })}</div>

      {!loading && !error && ordered.length > 0 ? <section className="plan-comparison-section" aria-labelledby="plan-comparison-title">
        <div className="split-heading plan-comparison-heading"><div><span className="section-kicker">Procurement detail</span><h2 id="plan-comparison-title">Need the full entitlement matrix?</h2></div><p>Keep the buying decision simple, then expand the detailed comparison when your finance, operations or procurement team needs it.</p></div>
        <details className="plan-comparison-disclosure">
          <summary><span><strong>Compare all plan capabilities</strong><small>{comparisonGroups.reduce((total, group) => total + group.rows.length, 0)} capabilities across {ordered.length} plans</small></span><ChevronDown aria-hidden="true"/></summary>
          <div className="plan-comparison-body">
            <div className="comparison-reading-guide"><strong>Read each capability from left to right.</strong><span>Start with the capability name, then follow the same row across Starter, Growth, Professional and Enterprise.</span></div>
            <div className="plan-comparison-scroll">
              <div className="comparison-matrix" style={{minWidth: `${Math.max(980, 320 + ordered.length * 170)}px`}}>
                <div className="comparison-grid comparison-grid-header" style={{gridTemplateColumns: `minmax(300px, 1.7fr) repeat(${ordered.length}, minmax(150px, 1fr))`}}>
                  <div className="comparison-capability-header">Capability</div>
                  {ordered.map(plan => <div className={plan.highlight ? 'comparison-plan-header comparison-plan-header-highlight' : 'comparison-plan-header'} key={plan.code}><strong>{plan.name}</strong><small>{plan.maxMembers || plan.max_members ? `Up to ${new Intl.NumberFormat().format(Number(plan.maxMembers || plan.max_members))} members` : 'Configured capacity'}</small></div>)}
                </div>
                {comparisonGroups.map(group => <section className="comparison-group" key={group.label}>
                  <div className="comparison-group-title">{group.label}</div>
                  {group.rows.map(row => <div className="comparison-grid comparison-feature-row" style={{gridTemplateColumns: `minmax(300px, 1.7fr) repeat(${ordered.length}, minmax(150px, 1fr))`}} key={row.code}>
                    <div className="comparison-feature-name">{row.label}</div>
                    {ordered.map(plan => {
                      const included = planHasFeature(plan, row.code);
                      return <div className={plan.highlight ? 'comparison-plan-cell comparison-plan-cell-highlight' : 'comparison-plan-cell'} key={`${plan.code}-${row.code}`}>
                        {included ? <span className="comparison-yes" aria-label={`${row.label} is included in ${plan.name}`}><Check/><em>Included</em></span> : <span className="comparison-no" aria-label={`${row.label} is not included in ${plan.name}`}><Minus/><em>Not included</em></span>}
                      </div>;
                    })}
                  </div>)}
                </section>)}
              </div>
            </div>
            <p className="comparison-note">Country-specific payment rails, regulatory reports, data residency, messaging and external integrations depend on availability in the cooperative’s operating country and may require separate implementation.</p>
          </div>
        </details>
      </section> : null}

      <section className="pricing-cost-guidance"><article><h3>Payment processing</h3><p>Gateway and banking charges are separate from the CoopCore subscription and depend on the selected provider.</p></article><article><h3>Messaging usage</h3><p>SMS, WhatsApp or other paid delivery channels are billed according to actual provider usage.</p></article><article><h3>Implementation</h3><p>Basic onboarding can be included, while migration, configuration, training and custom reports are scoped separately.</p></article><article><h3>Country requirements</h3><p>Country-specific compliance, settlement, payroll, bank or regulatory integrations are confirmed during implementation.</p></article></section>

      <div className="custom-plan-panel"><div><span className="section-kicker">Need a tailored arrangement?</span><h2>Custom plans can match a cooperative’s operating model.</h2><p>Discuss member volume, branches, countries, integrations, migration, support and workflow priorities with the CoopCore team.</p></div><Link className="button button-primary" to="/contact">Request a plan recommendation <ArrowRight/></Link></div>
    </div></section>
  </>;
}
